<template class="mt-4">
    <v-hover v-slot:default="{ hover }">
        <v-card
            class="roommate-card mx-auto pa-0"
            :class="[
                $vuetify.breakpoint.smAndDown ? 'mobile' : '',
                preview ? 'preview-mode' : '',
            ]"
            :height="preview ? 'auto' : 256"
            :max-height="preview ? 'none' : 256"
            :max-width="preview ? 500 : 450"
            :width="preview ? 500 : undefined"
            :elevation="preview ? 3 : hover ? 16 : 3"
        >
            <v-card-title class="pa-0">
                <div class="avatar-wrapper">
                    <v-avatar size="80" class="roommate-avatar">
                        <v-img :src="roommate.image_url">
                            <template v-slot:placeholder>
                                <v-avatar size="80" color="grey lighten-2">
                                    <v-icon color="grey lighten-1" size="25"
                                        >mdi-account-outline</v-icon
                                    >
                                </v-avatar>
                            </template>
                        </v-img>
                    </v-avatar>
                    <v-btn
                        fab
                        v-if="!type"
                        elevation="0"
                        class="favorite-icon"
                        :class="{ 'preview-disabled': preview }"
                        @click.stop="!preview && toggleLike(roommate.id)"
                        width="28"
                        height="28"
                        color="rgba(255,255,255,0.9)"
                        :disabled="preview"
                    >
                        <v-icon
                            size="16"
                            :color="
                                roommate.favorited_by_users &&
                                roommate.favorited_by_users.length > 0
                                    ? '#FF4141'
                                    : 'rgba(0,0,0,0.75)'
                            "
                            >{{
                                roommate.favorited_by_users &&
                                roommate.favorited_by_users.length > 0
                                    ? "mdi-heart"
                                    : "mdi-heart-outline"
                            }}</v-icon
                        >
                    </v-btn>
                </div>

                <div class="roommate-title d-flex flex-column">
                    <span>{{ roommate.name }}</span>
                    <span>{{ dob(roommate.date_of_birth) }}</span>
                </div>

                <v-tooltip v-if="roommate.school_name" bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <div class="school-logo-badge" v-bind="attrs" v-on="on">
                            <v-img
                                v-if="roommate.school_logo_url"
                                :src="roommate.school_logo_url"
                                contain
                                width="36"
                                height="36"
                            >
                                <template v-slot:placeholder>
                                    <v-icon size="18" color="grey lighten-1"
                                        >mdi-school</v-icon
                                    >
                                </template>
                            </v-img>
                            <v-icon v-else size="22" color="grey"
                                >mdi-school</v-icon
                            >
                        </div>
                    </template>
                    <span>{{ roommate.school_name }}</span>
                </v-tooltip>
            </v-card-title>

            <v-card-text class="mt-8 d-flex flex-column">
                <v-row class="flex-grow-1">
                    <v-col cols="6" class="pb-0">
                        <v-row>
                            <v-col class="pb-0 pt-4">
                                <h4>Interests:</h4>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col class="pt-0">
                                <div v-for="(item, i) in hobbies()" :key="i">
                                    <p
                                        v-if="i <= maxHobbiesCount"
                                        class="py-0 black--text hoobies"
                                    >
                                        {{ item }}
                                    </p>
                                </div>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="6" class="pb-4">
                        <v-row>
                            <v-col class="pb-0 pt-4">
                                <h4>School Start Date:</h4>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col class="pt-0 pb-0">
                                <p class="black--text">
                                    {{ roommate.university_start_date | date }}
                                </p>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" class="d-flex pb-0">
                                <a
                                    :href="`https://instagram.com/${formatInstagramUsername(
                                        roommate.instagram_username
                                    )}`"
                                    target="_blank"
                                    v-if="roommate.instagram_username"
                                >
                                    <v-img
                                        class="mr-3"
                                        contain
                                        max-width="25"
                                        max-height="25"
                                        src="@/assets/icons/social-instagram-sm.png"
                                    ></v-img>
                                </a>
                                <a
                                    :href="`https://tiktok.com/${formatTikTokUsername(
                                        roommate.tiktok_username
                                    )}`"
                                    target="_blank"
                                    v-if="roommate.tiktok_username"
                                >
                                    <v-img
                                        class="mr-3"
                                        contain
                                        max-width="25"
                                        max-height="25"
                                        src="@/assets/icons/social-tiktok-sm.png"
                                    ></v-img>
                                </a>
                                <a
                                    :href="`https://story.snapchat.com/u/${roommate.snapchat_username}`"
                                    target="_blank"
                                    v-if="roommate.snapchat_username"
                                >
                                    <v-img
                                        class="mr-3"
                                        contain
                                        max-width="25"
                                        max-height="25"
                                        src="@/assets/icons/social-snapchat-sm.png"
                                    ></v-img>
                                </a>
                                <a
                                    :href="`https://facebook.com/${formatFacebookUsername(
                                        roommate.facebook_username
                                    )}`"
                                    target="_blank"
                                    v-if="roommate.facebook_username"
                                >
                                    <v-img
                                        contain
                                        max-width="25"
                                        max-height="25"
                                        src="@/assets/icons/social-facebook-sm.png"
                                    ></v-img>
                                </a>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row :class="['mt-auto', preview ? 'pt-4' : 'pt-2']">
                    <v-col cols="6" class="text-me pt-0">
                        <h4>Text Me:</h4>
                        <a
                            v-if="!preview"
                            :href="`sms:+1 ${formattedPhoneNumber(roommate.phone_number)}`"
                        >
                            <v-icon small class="mr-1">mdi-message-text</v-icon>
                            {{ formattedPhoneNumber(roommate.phone_number) }}
                        </a>
                        <span v-else class="preview-disabled-link">
                            <v-icon small class="mr-1">mdi-message-text</v-icon>
                            {{ formattedPhoneNumber(roommate.phone_number) }}
                        </span>
                    </v-col>
                    <v-col cols="6" class="pt-0" v-if="!isRequest && !preview">
                        <ContactRoommateDialog
                            :name="roommate.name"
                            :roommateId="roommate.id"
                        />
                    </v-col>
                    <v-col
                        cols="6"
                        class="pt-0"
                        v-else-if="!isRequest && preview"
                    >
                        <v-btn
                            depressed
                            height="41"
                            class="btn-edurent preview-disabled"
                            color="primary"
                            disabled
                            >Match with Me</v-btn
                        >
                    </v-col>
                    <v-col cols="6" class="pt-0" v-else>
                        <div class="mb-4" v-if="type === 'received'">
                            <v-btn
                                depressed
                                height="41"
                                class="btn-edurent m-4"
                                color="primary"
                                @click="addRoommate"
                            >
                                Accept
                            </v-btn>
                            <v-btn
                                depressed
                                height="41"
                                class="btn-edurent ml-4 white--text"
                                color="red"
                                @click="declineRoommate"
                            >
                                Decline
                            </v-btn>
                        </div>
                        <div v-else>
                            <v-btn
                                depressed
                                height="41"
                                class="btn-edurent"
                                color="primary"
                                @click="declineRoommate"
                            >
                                Cancel Request
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-hover>
</template>

<script>
import moment from "moment";
import ContactRoommateDialog from "./ContactRoommateDialog";
import UserService from "../services/user.service";
import { formatPhoneNumber } from "../utils/utils";

export default {
    name: "RoommateCard",
    props: {
        roommate: Object,
        isRequest: Boolean,
        type: String,
        requestId: [String, Number],
        preview: {
            type: Boolean,
            default: false,
        },
    },
    components: { ContactRoommateDialog },
    data: () => ({
        colors: [
            "indigo",
            "warning",
            "pink darken-2",
            "red lighten-1",
            "deep-purple accent-4",
        ],
        slides: ["First", "Second", "Third", "Fourth", "Fifth"],
        maxHobbiesCount: 2,
    }),
    computed: {
        user() {
            return this.$store.getters.getUser;
        },
        isUserLoggedIn() {
            return this.$store.getters.isUserLoggedIn;
        },
        usersFavoriteRoommates() {
            if (this.isUserLoggedIn) {
                return this.$store.getters.getFavoriteRoommates.map(
                    (user) => user.id
                );
            }
            return [];
        },
        schools() {
            return this.$store.getters.getSchools;
        },
        roommateSchool() {
            if (!this.roommate.school_id) return null;
            return (
                this.schools.find(
                    (school) => school.id === this.roommate.school_id
                ) || null
            );
        },
        roommateSubtitle() {
            if (!this.roommate || !this.roommate.id) return "";

            const subtitleArray = [];
            if (this.roommate.date_of_birth) {
                subtitleArray.push(this.dob(this.roommate.date_of_birth));
            }
            if (this.roommate.gender) {
                subtitleArray.push(this.gender(this.roommate.gender));
            }
            if (this.roommate.school_id) {
                subtitleArray.push(this.schoolName(this.roommate.school_id));
            }
            return subtitleArray.join(" | ");
        },
    },
    methods: {
        async addRoommate() {
            await UserService.addRoommate(this.user.id, {
                requestId: this.requestId,
                roommateId: this.roommate.id,
            });
            await this.$nextTick();
            this.$store.dispatch("getRoommateRequests");
            this.$store.dispatch("getUserRoommates");
        },
        async declineRoommate() {
            await UserService.declineRoommate(this.user.id, {
                requestId: this.requestId,
            });
            await this.$nextTick();
            this.$store.dispatch("getRoommateRequests");
            this.$store.dispatch("getUserRoommates");
        },
        async removeRoommate() {
            await UserService.removeRoommate(this.user.id, {
                roommateId: this.roommate.id,
            });
            await this.$nextTick();
            this.$store.dispatch("getUserRoommates");
        },
        gender(value) {
            if (value && typeof value === "string") {
                return value.charAt(0).toUpperCase();
            }
            return null;
        },
        dob(value) {
            if (value) {
                return moment().diff(String(value), "years", false);
            }
        },
        schoolName(id) {
            if (id) {
                const school = this.schools
                    .filter((school) => school.id === id)
                    .shift();
                return school && school.name ? school.name : null;
            }
        },
        isUserFavorited(user) {
            return this.usersFavoriteRoommates.includes(user.id);
        },
        hobbies() {
            return this.roommate.hobbies !== null
                ? this.roommate.hobbies.split(",")
                : "";
        },
        async toggleLike(roommateId) {
            if (!this.isUserLoggedIn) {
                this.$store.dispatch("setSignInModal", true);
                return;
            }

            await this.$store.dispatch(
                "toggleFavoriteRoommateLike",
                roommateId
            );

            this.$store.dispatch("getUserRoommates");
            this.$store.dispatch("getMatchedRoommates");
            this.$store.dispatch("getFavoriteRoommates");
            this.$store.dispatch("getContactedRoommates");
        },
        formatInstagramUsername(name) {
            if (name.charAt(0) === "@") return name.slice(1);
            return name;
        },
        formattedPhoneNumber(phone) {
            return phone ? formatPhoneNumber(phone) : "";
        },
        formatFacebookUsername(name) {
            if (name.trim().includes(" ")) {
                const names = name.trim().split(" ");
                return names[0] + names[1];
            }

            return name;
        },
        formatTikTokUsername(name) {
            if (name.charAt(0) !== "@") return "@" + name;
            return name;
        },
    },
    filters: {
        date: function (value) {
            if (value) {
                return moment(String(value)).format("MMMM DD YYYY");
            }
        },
    },
};
</script>

<style scoped>
.roommate-card .v-card__title {
    height: 75px;
    position: relative;
}
.avatar-wrapper {
    position: absolute;
    top: 15px;
    left: 15px;
}
.roommate-avatar {
    margin: 0;
}
.school-logo-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    background: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    overflow: hidden;
}
.roommate-card .roommate-title {
    padding-left: 110px;
    padding-right: 55px;
    max-width: 100%;
    overflow: hidden;
}
.roommate-card .roommate-title span:first-child {
    font-size: 16px !important;
    font-weight: 700;
    line-height: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
}
.roommate-card .roommate-title span:last-child {
    font-size: 14px !important;
    font-weight: 400;
    line-height: 21px;
}
.v-application .headline {
    font-size: 1rem !important;
}
.roommate-card {
    position: relative;
    border-radius: 10px !important;
    transition: margin 0.3s ease-in-out;
}
.roommate-card .v-card__title {
    position: relative;
}
.favorite-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    z-index: 2;
}
.remove-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 1;
}

.roommate-card:hover {
    margin-top: -5px;
}
.roommate-card.mobile:hover {
    margin-top: 0px;
}
.roommate-card .properties-name-price {
    font-size: 16px !important;
    font-weight: 700;
    line-height: 24px;
}

.roommate-card .properties-details {
    font-size: 14px;
    line-height: 21px;
}

.roommate-card .v-card__title {
    background: #f4f5f6;
    position: relative;
}

.roommate-card .v-card__text {
    height: 159px;
}
.roommate-card .distance-from {
    position: absolute;
    bottom: 10px;
    left: 10px;
    z-index: 1;
    font-size: 12px;
    line-height: 12px;
    color: white;
}

.roommate-card h4 {
    font-size: 14px !important;
    font-weight: 700;
    color: #000;
}
.roommate-card .text-me p {
    font-size: 14px !important;
    font-weight: 400;
    color: #1976d2;
}
.roommate-card .hoobies {
    line-height: 20px;
}

/* Preview mode styles */
.roommate-card.preview-mode {
    cursor: default;
}
.roommate-card.preview-mode:hover {
    margin-top: 0;
}
.roommate-card.preview-mode .v-card__text {
    height: auto;
    padding-bottom: 16px;
}
.preview-disabled {
    cursor: not-allowed !important;
}
.preview-disabled-link {
    color: #999;
    font-size: 14px;
}

/* Keep button colors when disabled in preview mode */
.roommate-card.preview-mode .v-btn.preview-disabled.v-btn--disabled {
    background-color: #1976d2 !important;
    color: white !important;
    opacity: 0.7;
}
.roommate-card.preview-mode .favorite-icon.preview-disabled.v-btn--disabled {
    background-color: rgba(255, 255, 255, 0.9) !important;
    opacity: 0.7;
}
.roommate-card.preview-mode .favorite-icon.preview-disabled .v-icon {
    color: rgba(0, 0, 0, 0.75) !important;
}

@media (max-width: 600px) {
    .roommate-card.preview-mode {
        width: 100% !important;
        max-width: 100% !important;
    }
}
</style>
